export const guideNavigation = [
  { id: 'overview', label: 'Overview' },
  { id: 'sap-activate', label: 'SAP Activate' },
  { id: 'deliverables', label: 'Deliverables' },
  { id: 'how-it-works', label: 'How It Works' },
  { id: 'quickstarts', label: 'Quickstarts' },
  { id: 'faq', label: 'FAQ' },
];

export const valuePillars = [
  {
    title: 'Plan project work',
    description: 'Organize project activities around familiar SAP Activate phases.',
  },
  {
    title: 'Capture project context',
    description: 'Use documents, templates, and requirements as reusable project inputs.',
  },
  {
    title: 'Generate deliverables',
    description: 'Create structured drafts that consultants can review, refine, and export.',
  },
];

export const activatePhases = [
  {
    name: 'Prepare',
    color: '#F5821F',
    summary: 'Plan the project foundation before detailed design begins.',
    outputs: ['Project charter', 'Workshop schedule', 'RACI matrix', 'Scope baseline'],
  },
  {
    name: 'Explore',
    color: '#4DB6AC',
    summary: 'Capture fit-to-standard outcomes, gaps, and business process understanding.',
    outputs: ['Fit-to-standard notes', 'Gap analysis', 'BPML and BPD', 'Business requirements'],
  },
  {
    name: 'Realize',
    color: '#9C27B0',
    summary: 'Turn approved requirements and gaps into build-ready specifications and test assets.',
    outputs: ['RICEFW list', 'Functional specs', 'Technical specs', 'UAT test scripts'],
  },
  {
    name: 'Deploy',
    color: '#FF5722',
    summary: 'Support readiness, cutover, training, and release preparation.',
    outputs: ['Training notes', 'Cutover plan', 'Go-live checklist', 'Deployment readiness'],
  },
  {
    name: 'Run',
    color: '#00BCD4',
    summary: 'Help teams transition into hypercare, support, and knowledge transfer.',
    outputs: ['Hypercare notes', 'Knowledge transfer', 'Support handover', 'Improvement backlog'],
  },
];

export const capabilities = [
  {
    title: 'Project Workspace',
    description: 'Manage project context, documents, stakeholders, and phase progress.',
  },
  {
    title: 'Document Intelligence',
    description: 'Extract useful scope, timeline, and requirement signals from project documents.',
  },
  {
    title: 'Template-Based Generation',
    description: 'Use approved formats so outputs stay consistent across projects and teams.',
  },
  {
    title: 'Review and Export',
    description: 'Keep outputs editable so teams can validate and share client-ready documents.',
  },
];

export const deliverableGroups = [
  {
    phase: 'Foundation',
    color: '#90A4AE',
    items: ['Project Insights', 'Scope Summary', 'Stakeholder Summary'],
  },
  {
    phase: 'Prepare',
    color: '#F5821F',
    items: ['Project Charter', 'Workshop Schedule', 'RACI Matrix'],
  },
  {
    phase: 'Explore',
    color: '#4DB6AC',
    items: ['Fit-to-Standard Notes', 'Gap Analysis', 'BPML / BPD'],
  },
  {
    phase: 'Realize',
    color: '#9C27B0',
    items: ['RICEFW List', 'Functional Specification', 'Technical Specification', 'UAT Test Scripts'],
  },
];

export const howItWorks = [
  'Create or select a project.',
  'Upload project documents.',
  'Review extracted insights.',
  'Generate phase deliverables.',
  'Review, refine, and export.',
];

export const quickstarts = [
  {
    title: 'Analyze Project Documents',
    steps: ['Open Project Overview', 'Upload project documents', 'Review extracted scope, timeline, assumptions, and risks'],
  },
  {
    title: 'Create a Project Charter',
    steps: ['Open Prepare', 'Select Project Charter', 'Generate a draft using project context and approved templates'],
  },
  {
    title: 'Build a RACI Matrix',
    steps: ['Open Prepare', 'Choose RACI Matrix', 'Review roles, owners, and responsibilities'],
  },
  {
    title: 'Generate a Gap Analysis',
    steps: ['Open Explore', 'Use workshop or requirement inputs', 'Review gaps, impacts, and recommended actions'],
  },
  {
    title: 'Create a Functional Specification',
    steps: ['Open Realize', 'Review the related RICEFW item', 'Generate and refine the functional specification'],
  },
  {
    title: 'Generate UAT Test Scripts',
    steps: ['Open Realize', 'Select UAT Test Scripts', 'Generate steps tied to business scenarios and requirements'],
  },
];

export const faqs = [
  {
    question: 'Can clients use their own templates?',
    answer: 'Yes. The intended model is to support approved client or partner templates so generated documents match expected formats.',
  },
  {
    question: 'What inputs produce the best results?',
    answer: 'Project documents, process notes, workshop outputs, requirement lists, RICEFW trackers, and approved templates provide the strongest project context.',
  },
  {
    question: 'Are generated outputs editable?',
    answer: 'Yes. Outputs should be treated as review-ready drafts that project teams can edit, validate, export, and share.',
  },
  {
    question: 'Which SAP phases are supported?',
    answer: 'The current experience emphasizes foundation, Prepare, Explore, and Realize activities, with Deploy and Run content positioned for future extension.',
  },
];
